import { STAFF_ROLES, UserRole } from '@nebalkon-workspace/auth';
import {
  DEFAULT_WEB_CABINET_PATHS,
  type CabinetPaths,
} from '@nebalkon-workspace/cabinet-core';

export interface StaffCabinetNavItem {
  labelKey: string;
  route: string;
  exact?: boolean;
  roles: UserRole[];
  icon?: string;
}

export const STAFF_CABINET_NAV_ICONS = {
  '': '/icons/tab-home.svg',
  orders: '/icons/core/order-box.svg',
  more: '/icons/core/burger-big.svg',
  scan: '/icons/core/scan-qr.svg',
  warehouses: '/icons/core/warehouse-green.svg',
  employees: '/icons/core/essential-user-green.svg',
  users: '/icons/user-icon.svg',
  'box-types': '/icons/core/order-box.svg',
  pricing: '/icons/cabinet/quick-actions/tariffs.svg',
  leads: '/icons/core/phone.svg',
  settings: '/icons/core/gear.svg',
} as const;

/** Routes pinned to the mobile bottom tab bar (not shown in «Ещё»). */
export const STAFF_CABINET_BOTTOM_TAB_ROUTES = ['', 'orders', 'more'] as const;

/** Section list routes opened from the mobile «Ещё» menu. */
export const STAFF_CABINET_MORE_SECTION_ROOTS = [
  'warehouses',
  'employees',
  'users',
  'box-types',
  'pricing',
  'settings',
  'leads',
  'scan',
] as const;

export interface StaffCabinetBackRouteOptions {
  /** When true, section roots from «Ещё» navigate back to /more. */
  mobileLayout?: boolean;
}

export const STAFF_CABINET_PRIMARY_NAV: StaffCabinetNavItem[] = [
  {
    labelKey: 'STAFF.CABINET.NAV.HOME',
    route: '',
    exact: true,
    icon: STAFF_CABINET_NAV_ICONS[''],
    roles: [...STAFF_ROLES],
  },
  {
    labelKey: 'STAFF.CABINET.NAV.ORDERS',
    route: 'orders',
    icon: STAFF_CABINET_NAV_ICONS.orders,
    roles: [...STAFF_ROLES],
  },
  {
    labelKey: 'STAFF.CABINET.NAV.LEADS',
    route: 'leads',
    icon: STAFF_CABINET_NAV_ICONS.leads,
    roles: [UserRole.Admin, UserRole.Manager],
  },
  {
    labelKey: 'STAFF.CABINET.NAV.SCAN',
    route: 'scan',
    icon: STAFF_CABINET_NAV_ICONS.scan,
    roles: [UserRole.Manager, UserRole.WarehouseWorker],
  },
  {
    labelKey: 'STAFF.CABINET.NAV.WAREHOUSES',
    route: 'warehouses',
    icon: STAFF_CABINET_NAV_ICONS.warehouses,
    roles: [UserRole.Admin, UserRole.Manager, UserRole.WarehouseWorker],
  },
  {
    labelKey: 'STAFF.CABINET.NAV.EMPLOYEES',
    route: 'employees',
    icon: STAFF_CABINET_NAV_ICONS.employees,
    roles: [UserRole.Admin],
  },
  {
    labelKey: 'STAFF.CABINET.NAV.USERS',
    route: 'users',
    icon: STAFF_CABINET_NAV_ICONS.users,
    roles: [UserRole.Admin, UserRole.Manager],
  },
  {
    labelKey: 'STAFF.CABINET.NAV.BOX_TYPES',
    route: 'box-types',
    icon: STAFF_CABINET_NAV_ICONS['box-types'],
    roles: [UserRole.Admin],
  },
  {
    labelKey: 'STAFF.CABINET.NAV.PRICING',
    route: 'pricing',
    icon: STAFF_CABINET_NAV_ICONS.pricing,
    roles: [UserRole.Admin, UserRole.Manager],
  },
  {
    labelKey: 'STAFF.CABINET.NAV.SETTINGS',
    route: 'settings',
    icon: STAFF_CABINET_NAV_ICONS.settings,
    roles: [UserRole.Admin, UserRole.Manager],
  },
];

export type StaffCabinetMobileTabItem =
  | {
      type: 'nav';
      labelKey: string;
      route: string;
      icon: string;
      exact?: boolean;
    }
  | { type: 'qr'; icon: string };

const STAFF_CABINET_MOBILE_QR_TAB: StaffCabinetMobileTabItem = {
  type: 'qr',
  icon: STAFF_CABINET_NAV_ICONS.scan,
};

const STAFF_CABINET_MORE_TAB: StaffCabinetMobileTabItem = {
  type: 'nav',
  labelKey: 'STAFF.CABINET.NAV.MORE',
  route: 'more',
  icon: STAFF_CABINET_NAV_ICONS.more,
};

const STAFF_CABINET_MOBILE_HOME_TAB: StaffCabinetMobileTabItem = {
  type: 'nav',
  labelKey: 'STAFF.CABINET.NAV.HOME',
  route: '',
  icon: STAFF_CABINET_NAV_ICONS[''],
  exact: true,
};

const STAFF_CABINET_MOBILE_ORDERS_TAB: StaffCabinetMobileTabItem = {
  type: 'nav',
  labelKey: 'STAFF.CABINET.NAV.ORDERS',
  route: 'orders',
  icon: STAFF_CABINET_NAV_ICONS.orders,
};

const STAFF_QR_SCAN_ROLES: UserRole[] = [
  UserRole.Manager,
  UserRole.WarehouseWorker,
];

export function staffCabinetNavForRoles(roles: UserRole[]): StaffCabinetNavItem[] {
  return STAFF_CABINET_PRIMARY_NAV.filter((item) =>
    item.roles.some((role) => roles.includes(role)),
  );
}

export function staffCabinetMoreMenuItemsForRoles(
  roles: UserRole[],
): StaffCabinetNavItem[] {
  const bottomTabRoutes = new Set<string>(STAFF_CABINET_BOTTOM_TAB_ROUTES);

  return staffCabinetNavForRoles(roles).filter(
    (item) => !bottomTabRoutes.has(item.route),
  );
}

export function buildStaffCabinetMobileBottomTabs(
  roles: UserRole[],
): StaffCabinetMobileTabItem[] {
  const items: StaffCabinetMobileTabItem[] = [
    STAFF_CABINET_MOBILE_HOME_TAB,
    STAFF_CABINET_MOBILE_ORDERS_TAB,
  ];

  if (roles.some((role) => STAFF_QR_SCAN_ROLES.includes(role))) {
    items.push(STAFF_CABINET_MOBILE_QR_TAB);
  }

  items.push(STAFF_CABINET_MORE_TAB);

  return items;
}

/** @deprecated Use {@link buildStaffCabinetMobileBottomTabs} */
export function staffCabinetMobileBottomTabsForRoles(
  roles: UserRole[],
): StaffCabinetNavItem[] {
  return buildStaffCabinetMobileBottomTabs(roles)
    .filter((item): item is Extract<StaffCabinetMobileTabItem, { type: 'nav' }> =>
      item.type === 'nav',
    )
    .map((item) => ({
      labelKey: item.labelKey,
      route: item.route,
      exact: item.exact,
      icon: item.icon,
      roles: [...STAFF_ROLES],
    }));
}

/** @deprecated Use {@link buildStaffCabinetMobileBottomTabs} */
export const STAFF_CABINET_MOBILE_BOTTOM_TABS: StaffCabinetNavItem[] =
  buildStaffCabinetMobileBottomTabs([...STAFF_ROLES])
    .filter((item): item is Extract<StaffCabinetMobileTabItem, { type: 'nav' }> =>
      item.type === 'nav',
    )
    .map((item) => ({
      labelKey: item.labelKey,
      route: item.route,
      exact: item.exact,
      icon: item.icon,
      roles: [...STAFF_ROLES],
    }));

export function staffCabinetNavLink(
  route: string,
  paths: CabinetPaths = DEFAULT_WEB_CABINET_PATHS,
): string {
  if (!route) {
    return paths.staff;
  }

  return `${paths.staff}/${route}`;
}

export function normalizeStaffCabinetPath(url: string): string {
  return url.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
}

export function isStaffCabinetNavActive(
  currentUrl: string,
  item: StaffCabinetNavItem,
  paths: CabinetPaths = DEFAULT_WEB_CABINET_PATHS,
): boolean {
  const normalized = normalizeStaffCabinetPath(currentUrl);
  const link = staffCabinetNavLink(item.route, paths);

  if (item.exact) {
    return normalized === link;
  }

  return normalized === link || normalized.startsWith(`${link}/`);
}

function staffCabinetBasePath(paths: CabinetPaths): string {
  return paths.staff.replace(/\/+$/, '');
}

export function staffCabinetRelativePath(
  path: string,
  paths: CabinetPaths = DEFAULT_WEB_CABINET_PATHS,
): string | null {
  const normalized = normalizeStaffCabinetPath(path.split('?')[0] ?? path);
  const staffBase = staffCabinetBasePath(paths);

  if (normalized === staffBase) {
    return '';
  }

  if (normalized.startsWith(`${staffBase}/`)) {
    return normalized.slice(staffBase.length + 1);
  }

  return null;
}

export function isStaffCabinetTabRoot(
  path: string,
  paths: CabinetPaths = DEFAULT_WEB_CABINET_PATHS,
): boolean {
  const relative = staffCabinetRelativePath(path, paths);

  if (relative === null) {
    return false;
  }

  return relative === '' || relative === 'orders' || relative === 'more';
}

/** Tab-root routes hide page chrome header (title/back row). */
export function shouldHideStaffCabinetPageHeader(
  path: string,
  paths: CabinetPaths = DEFAULT_WEB_CABINET_PATHS,
): boolean {
  return isStaffCabinetTabRoot(path, paths);
}

function moreSectionBackRoute(
  staffBase: string,
  options: StaffCabinetBackRouteOptions,
): string | null {
  return options.mobileLayout ? `${staffBase}/more` : null;
}

export function resolveStaffCabinetBackRoute(
  path: string,
  paths: CabinetPaths = DEFAULT_WEB_CABINET_PATHS,
  options: StaffCabinetBackRouteOptions = {},
): string | null {
  const staffBase = staffCabinetBasePath(paths);
  const relative = staffCabinetRelativePath(path, paths);

  if (relative === null || isStaffCabinetTabRoot(path, paths)) {
    return null;
  }

  const segments = relative.split('/').filter(Boolean);
  const [first, second, third, fourth, fifth] = segments;

  if (
    options.mobileLayout &&
    segments.length === 1 &&
    STAFF_CABINET_MORE_SECTION_ROOTS.includes(
      first as (typeof STAFF_CABINET_MORE_SECTION_ROOTS)[number],
    )
  ) {
    return `${staffBase}/more`;
  }

  if (first === 'scan') {
    return moreSectionBackRoute(staffBase, options);
  }

  if (first === 'leads') {
    if (segments.length === 1) {
      return moreSectionBackRoute(staffBase, options);
    }
    if (segments.length === 2) {
      return `${staffBase}/leads`;
    }
    return `${staffBase}/leads/${encodeURIComponent(second)}`;
  }

  if (first === 'orders' && segments.length >= 2) {
    return `${staffBase}/orders`;
  }

  if (first === 'warehouses') {
    if (segments.length === 1) {
      return moreSectionBackRoute(staffBase, options);
    }
    if (second === 'create') {
      return `${staffBase}/warehouses`;
    }
    if (second === 'slug' && third) {
      if (segments.length === 3) {
        return `${staffBase}/warehouses`;
      }
      if (fourth === 'cells' && fifth) {
        return `${staffBase}/warehouses/slug/${encodeURIComponent(third)}`;
      }
    }
    if (third === 'edit') {
      return `${staffBase}/warehouses/${encodeURIComponent(second)}`;
    }
    if (third === 'placement') {
      return `${staffBase}/warehouses/${encodeURIComponent(second)}`;
    }
    if (third === 'cells' && fourth) {
      return `${staffBase}/warehouses/${encodeURIComponent(second)}`;
    }
    if (segments.length === 2) {
      return `${staffBase}/warehouses`;
    }
  }

  if (first === 'employees') {
    if (segments.length === 1) {
      return moreSectionBackRoute(staffBase, options);
    }
    if (second === 'create') {
      return `${staffBase}/employees`;
    }
    if (third === 'edit') {
      return `${staffBase}/employees/${encodeURIComponent(second)}`;
    }
    if (segments.length === 2) {
      return `${staffBase}/employees`;
    }
  }

  if (first === 'users') {
    if (segments.length === 1) {
      return moreSectionBackRoute(staffBase, options);
    }
    if (second === 'new') {
      return `${staffBase}/users`;
    }

    const userBase = `${staffBase}/users/${encodeURIComponent(second)}`;

    if (segments.length === 2) {
      return `${staffBase}/users`;
    }

    if (third === 'items') {
      return userBase;
    }

    if (third === 'wallet' && fourth === 'statement') {
      return userBase;
    }

    if (third === 'profile') {
      if (segments.length === 3) {
        return `${staffBase}/users`;
      }
      if (fourth === 'edit' || fourth === 'legal-entity') {
        return userBase;
      }
      if (fourth === 'my-addresses') {
        if (segments.length === 4) {
          return userBase;
        }
        return `${userBase}/profile/my-addresses`;
      }
    }
  }

  if (first === 'box-types') {
    if (segments.length === 1) {
      return moreSectionBackRoute(staffBase, options);
    }
    if (second === 'create') {
      return `${staffBase}/box-types`;
    }
    if (segments.length === 2) {
      return `${staffBase}/box-types`;
    }
  }

  if (first === 'pricing') {
    if (segments.length === 1) {
      return moreSectionBackRoute(staffBase, options);
    }
    if (segments.length === 2) {
      return `${staffBase}/pricing`;
    }

    const domainBase = `${staffBase}/pricing/${encodeURIComponent(second)}`;

    if (segments.length === 4 && fourth === 'edit') {
      return `${domainBase}/${encodeURIComponent(third)}`;
    }

    return domainBase;
  }

  if (first === 'settings') {
    if (segments.length === 1) {
      return moreSectionBackRoute(staffBase, options);
    }
    if (second === 'printers') {
      return `${staffBase}/settings`;
    }
  }

  return null;
}

export function resolveStaffCabinetBackRouteFromUrl(
  url: string,
  paths: CabinetPaths = DEFAULT_WEB_CABINET_PATHS,
  options: StaffCabinetBackRouteOptions = {},
): string | null {
  const queryIndex = url.indexOf('?');
  const pathOnly = queryIndex >= 0 ? url.slice(0, queryIndex) : url;

  if (queryIndex >= 0) {
    const userId = new URLSearchParams(url.slice(queryIndex + 1))
      .get('user_id')
      ?.trim();
    const relative = staffCabinetRelativePath(pathOnly, paths);

    if (userId && relative === 'orders') {
      return `${staffCabinetBasePath(paths)}/users/${encodeURIComponent(userId)}`;
    }
  }

  return resolveStaffCabinetBackRoute(pathOnly, paths, options);
}
